import { gsap } from 'gsap';

import { cn } from '@/utils/cn';

import { useSectionReveal } from './useSectionReveal';

/**
 * The type is drawn as SVG rather than as HTML text.
 *
 * The face is Bebas Neue, loaded for this treatment and used nowhere else on
 * the site -- it has no lowercase and a single weight, so it is a wordmark
 * face rather than a text face. Even that is a little wider than the design
 * sets it, and it has no width axis to close the gap. Squeezing HTML text with
 * `transform: scaleX()` does not work here: the line is far wider than the viewport, an overflowing
 * `white-space: nowrap` line does not reliably honour `text-align: center`, and
 * scaling it around its box centre then throws it off-centre by whatever the
 * overflow happens to be. SVG's `textLength` with
 * `lengthAdjust="spacingAndGlyphs"` condenses the glyphs to an exact width
 * instead -- no measuring, no overflow, no dependence on the font's metrics,
 * and it stays exact while the webfont is still loading.
 *
 * Everything below is in viewBox units against a 1000-wide canvas, so each
 * value is also a per-mille of the section's width and the whole treatment
 * scales with the viewport for free.
 */
const VIEW_WIDTH = 1000;

/** Cap height, 8.7% of the width -- the design's 167px against a 1920 frame. */
const CAP = 87;

/**
 * Bebas Neue's cap height is 0.70em, so this is the size that yields CAP.
 *
 * Measured off the loaded face rather than taken from the spec sheet, and it
 * has to move with the family: at Inter's 0.727 the type sat 4% short of the
 * row it is cropped to.
 */
const FONT_SIZE = CAP / 0.7;

/** Baseline sits at CAP, which puts the cap top on y=0 and makes cropping trivial. */
const BASELINE = CAP;

/** Slightly inset from the full width, as the design has it. */
const TEXT_LENGTH = 940;

/**
 * How much of each row survives, and how far it has receded.
 *
 * The first row is whole; the ones behind it keep just over half their cap
 * height and dim as they go, which is what makes the stack read as one line
 * echoing backwards rather than four lines of the same text.
 *
 * They are cropped from the *top*: every row shares a bottom edge, so what
 * survives on the echoes is the foot of each letter, not its head. That is the
 * whole illusion -- the line looks like it is sinking out of the page rather
 * than rising into it, and cropping the other way reads as four separate lines
 * stacked up.
 *
 * The crop is the SVG's own viewport -- an SVG clips to its viewBox already,
 * so no extra overflow container is needed, and shifting the viewBox's y down
 * by the height a row gives up is all it takes to keep the bottom edges
 * aligned.
 */
const ROWS = [
  { height: 91, opacity: 1 },
  { height: 48, opacity: 0.42 },
  { height: 48, opacity: 0.3 },
  { height: 48, opacity: 0.22 },
];

/** Gap between rows, as a share of the width so it scales with the type. */
const ROW_GAP = '0.83%';

/**
 * The stretch, then the settle.
 *
 * Starts pulled downwards -- `transform-origin` is the top edge, so scaleY
 * above 1 elongates towards the bottom rather than growing in both directions
 * -- and narrowed slightly, the way something under tension thins. From there
 * it overshoots into a squash and rings down.
 *
 * Authored as explicit stages rather than left to an elastic ease: at this
 * type size the overshoot `elastic.out` picks reads as cartoonish, and here
 * every extreme is a number someone chose. The shrinking durations do the
 * damping.
 */
const FROM = { scaleX: 0.88, scaleY: 1.75, opacity: 0, filter: 'blur(10px)' };

const STAGES = [
  { scaleX: 1.08, scaleY: 0.82, duration: 1.2, ease: 'power3.out' },
  { scaleX: 0.96, scaleY: 1.09, duration: 0.8, ease: 'power2.inOut' },
  { scaleX: 1.02, scaleY: 0.97, duration: 0.6, ease: 'power2.inOut' },
  { scaleX: 1, scaleY: 1, duration: 0.6, ease: 'power2.out' },
];

/** 3.2s per row plus three staggers puts the full sequence at 5.3s. */
const STAGGER = 0.7;

/**
 * Oversized typography that stretches down into place, echoing behind itself.
 *
 * The visible type is SVG and hidden from assistive tech; the accessible name
 * comes from a single visually-hidden copy of the string, so the heading is
 * announced once, in its original case, rather than four times in caps.
 */
export function EchoHeadline({ text, className }) {
  const root = useSectionReveal(({ timeline, select }) => {
    select('.js-echo-row').forEach((row, index) => {
      // Anchored at the top edge so the stretch reaches downwards.
      gsap.set(row, { transformOrigin: '50% 0%' });

      const bounce = gsap.timeline({
        // Landing on scale(1, 1) is not the same as landing on no transform at
        // all: the leftover matrix keeps the row on its own composited layer,
        // which softens type this large. Clearing it hands the text back to
        // the normal rasteriser.
        onComplete: () => gsap.set(row, { clearProps: 'transform,filter' }),
      });

      bounce.fromTo(row, FROM, {
        ...STAGES[0],
        opacity: ROWS[index].opacity,
        filter: 'blur(0px)',
      });

      STAGES.slice(1).forEach((stage) => bounce.to(row, stage));

      timeline.add(bounce, index * STAGGER);
    });
  });

  const display = text.toUpperCase();

  return (
    <h2 ref={root} className={cn('font-display select-none text-[#D7CEE2]', className)}>
      <span className="sr-only">{text}</span>

      {ROWS.map((row, index) => (
        <span
          key={row.opacity}
          className="js-echo-row block"
          style={{ opacity: row.opacity, marginTop: index === 0 ? undefined : ROW_GAP }}
        >
          <svg
            aria-hidden="true"
            viewBox={`0 ${ROWS[0].height - row.height} ${VIEW_WIDTH} ${row.height}`}
            className="block w-full"
          >
            <text
              x={VIEW_WIDTH / 2}
              y={BASELINE}
              textAnchor="middle"
              textLength={TEXT_LENGTH}
              lengthAdjust="spacingAndGlyphs"
              fontSize={FONT_SIZE}
              fontWeight="900"
              fill="currentColor"
            >
              {display}
            </text>
          </svg>
        </span>
      ))}
    </h2>
  );
}
